/**
 * /api/app/sys、/api/app/user、/api/app/pay 统一请求封装。
 * 后端返回 Result：msg、ret/status（即业务 code）、data。
 * code: 0 正常，1 提示，-1 错误，-99 登录过期需重新登录。
 */

export class AppResultError extends Error {
  constructor(message, code, payload) {
    super(message)
    this.name = 'AppResultError'
    this.code = code
    this.payload = payload
    this.handled = true
  }
}

function getResultCode(payload) {
  if (!payload || typeof payload !== 'object') return undefined
  if (payload.ret !== undefined && payload.ret !== null) return Number(payload.ret)
  if (payload.status !== undefined && payload.status !== null) return Number(payload.status)
  if (payload.code !== undefined && payload.code !== null) return Number(payload.code)
  return undefined
}

function emitMessage(type, text, duration) {
  if (!text) return
  window.dispatchEvent(new CustomEvent('app-message', { detail: { type, text, duration } }))
}

let authExpiredHandling = false

function handleAuthExpired(msg) {
  localStorage.removeItem('appToken')
  localStorage.removeItem('appUser')
  localStorage.removeItem('appDiamonds')
  if (authExpiredHandling) return
  authExpiredHandling = true
  emitMessage('error', msg || '登录已过期，请重新登录')
  window.dispatchEvent(new CustomEvent('app-auth-expired', { detail: { msg } }))
  setTimeout(() => {
    authExpiredHandling = false
  }, 1500)
}

export function handleAppResult(payload) {
  const code = getResultCode(payload)
  const msg = (payload && payload.msg) || ''

  if (code === 0) {
    return payload.data || {}
  }
  if (code === 1) {
    emitMessage('info', msg || '提示')
    return payload.data || {}
  }
  if (code === -99) {
    handleAuthExpired(msg)
    throw new AppResultError(msg || '登录已过期，请重新登录', code, payload)
  }

  emitMessage('error', msg || '请求失败')
  throw new AppResultError(msg || '请求失败', code, payload)
}

export function buildFormBody(data) {
  const body = new URLSearchParams()
  Object.keys(data || {}).forEach(key => {
    const value = data[key]
    if (value !== undefined && value !== null && value !== '') {
      body.append(key, value)
    }
  })
  return body
}

export async function appRequest(path, data = {}, options = {}) {
  const headers = {
    'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8',
    ...(options.headers || {})
  }
  const token = localStorage.getItem('appToken')
  if (token) {
    headers.token = token
  }

  const url = path.startsWith('http') ? path : (localStorage.baseUrl + path)
  let res
  try {
    res = await fetch(url, {
      method: options.method || 'POST',
      headers,
      body: options.body !== undefined ? options.body : buildFormBody(data)
    })
  } catch (e) {
    emitMessage('error', '网络连接失败')
    throw new AppResultError('网络连接失败', -1)
  }

  let payload
  try {
    payload = await res.json()
  } catch (e) {
    emitMessage('error', '服务器响应异常')
    throw new AppResultError('服务器响应异常', -1)
  }

  return handleAppResult(payload)
}

export function appSysRequest(path, data = {}, options = {}) {
  return appRequest('/api/app/sys' + path, data, options)
}

export function appUserRequest(path, data = {}, options = {}) {
  return appRequest('/api/app/user' + path, data, options)
}

export function appPayRequest(path, data = {}, options = {}) {
  return appRequest('/api/app/pay' + path, data, options)
}
